import { ClientSettingsProvider, type SipClientHooks } from "@dvrt/web-phone-client";
import { ConferenceCallPage } from "@dvrt/web-phone-client/call-widget";
import { showNotification } from "conference/actions";
import { getConferenceUri } from "conference/components/joinMeetingTools";
import { browserWithVersion, phoneClientLocalStorage, useDebugSettings } from "conference/tools";
import { useCurrentMeeting } from "../contexts/currentMeeting";
import { MeetingPageSettingsProvider, meetingPageSettingsStorage, useSettings } from "../tools";
import { MeetingPageStage } from "../types";
import { useDispatch } from "react-redux";
import { useMemo } from "react";
import merge from "lodash.merge";
import { useRoomLeaveProtection } from "./client/roomLeaveProtection";
import { getCallEndNotification } from "./callEnd";

const RoomContent = () => {
    const currentMeeting = useCurrentMeeting();
    const settings = useSettings();
    const debugSettings = useDebugSettings();
    const dispatch = useDispatch();
    const sipHooks = useMemo<SipClientHooks>(
        () => ({
            notification: (message, variant) => dispatch(showNotification({ message, variant })),
            beforeApplyCallOptions: (options) => merge(options, window.callOptionsOverrides),
            getVideoConstraints: (constraints) => merge(constraints, window.videoConstraintsOverrides),
        }),
        [dispatch],
    );
    useRoomLeaveProtection(!(debugSettings.enabled && debugSettings.disableRoomLeaveBlocker));

    return (
        <ConferenceCallPage
            userInfo={{
                displayName: settings.displayName,
                uri: currentMeeting.selfUri,
                audio: settings.audio,
                video: settings.video,
            }}
            conferenceInfo={{
                uri: getConferenceUri(currentMeeting.selfIdentifier),
                roomId: currentMeeting.chatRoomId,
            }}
            sip={{
                options: {
                    userAgent: `Deverto Conference/${browserWithVersion}`,
                    proxyUrl: window.tequet.proxyUrl,
                    auth: {
                        ...currentMeeting.selfSipUserAuth,
                        displayName: settings.displayName,
                    },
                    hooks: sipHooks,
                },
            }}
            chat={{
                baseUrl: window.chattoBaseUrl,
                fileUploadAuthHeader: `Invitation ${currentMeeting.selfIdentifier}`,
                token: currentMeeting.selfIdentifier,
            }}
            desktopShareConstraints={window.desktopShareConstraints}
            diagnostics={{
                showVideoInfo: debugSettings.enabled && debugSettings.showVideoInfo,
                speakerList: {
                    open: debugSettings.enabled && debugSettings.showSpeakerListInfoDialog,
                    onClose: () => debugSettings.update({ showSpeakerListInfoDialog: false }),
                },
            }}
            onFailed={() => {
                settings.update({ stage: MeetingPageStage.Prepare });
            }}
            onEnded={(result: { connected: boolean; error?: { cause: string; message?: string } }) => {
                const notification = getCallEndNotification(result.error);
                if (notification)
                    dispatch(
                        showNotification({
                            ...notification,
                            variant: "error",
                        }),
                    );
                settings.update({ stage: MeetingPageStage.LiftOff });
            }}
        />
    );
};

export const MeetingRoom = () => {
    return (
        <MeetingPageSettingsProvider settings={meetingPageSettingsStorage}>
            <ClientSettingsProvider settings={phoneClientLocalStorage}>
                <RoomContent />
            </ClientSettingsProvider>
        </MeetingPageSettingsProvider>
    );
};
